import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'src/components/common/ui/Modal';

import type { Task } from 'src/features/tasks/types/task.type';
import type { Risk } from 'src/features/risks/types/risk.type';

import { apiListTasks, apiCreateTask, apiUpdateTask, apiDeleteTask } from 'src/features/tasks/api/tasks.api';
import { apiListRisks, apiCreateRisk, apiUpdateRisk, apiDeleteRisk } from 'src/features/risks/api/risks.api';

import TaskEditorModal from 'src/features/tasks/components/TaskEditorModal';
import RiskEditorModal from 'src/features/risks/components/RiskEditorModal';

type Mode = 'risk' | 'task';

export type WbsMini = {
    /** WBS row id (버전 id) - UI 키 */
    id: string;
    /** 연결 키 */
    nodeId: string;
    code: string;
    name: string;
};

type Props = {
    open: boolean;
    onClose: () => void;
    projectId: string;
    item: WbsMini;
    mode: Mode;
    canEdit?: boolean;
};

export default function WbsLinksModal({ open, onClose, projectId, item, mode, canEdit = true }: Props) {
    const title = mode === 'risk' ? 'Linked Risks' : 'Linked Tasks';

    const [loading, setLoading] = useState(false);

    const [tasks, setTasks] = useState<Task[]>([]);
    const [risks, setRisks] = useState<Risk[]>([]);

    const [openTaskEditor, setOpenTaskEditor] = useState(false);
    const [openRiskEditor, setOpenRiskEditor] = useState(false);

    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [selectedRisk, setSelectedRisk] = useState<Risk | null>(null);

    const reload = useCallback(async () => {
        if (!open) return;
        if (!projectId || !item?.nodeId) return;

        setLoading(true);
        try {
            const [t, r] = await Promise.all([
                apiListTasks(projectId, { wbsNodeId: item.nodeId, includeInactive: true }),
                apiListRisks(projectId, { includeClosed: true }),
            ]);

            setTasks(t ?? []);
            setRisks((r ?? []).filter((x) => String(x.wbsNodeId ?? '') === String(item.nodeId)));
        } finally {
            setLoading(false);
        }
    }, [open, projectId, item?.nodeId]);

    useEffect(() => {
        if (open) reload();
    }, [open, reload]);

    const list = useMemo(() => {
        if (mode === 'task') return tasks;
        return risks;
    }, [mode, tasks, risks]);

    return (
        <Modal open={open} onClose={onClose} title={title} size="lg">
            <div className="space-y-3">
                <div className="text-xs text-gray-600">
                    <div className="font-semibold text-gray-900">
                        {item.code} {item.name}
                    </div>
                    <div className="mt-0.5">wbsNodeId: {item.nodeId}</div>
                </div>

                <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-600">{loading ? 'Loading...' : `${list.length} items`}</div>

                    {canEdit && (
                        <button
                            className="text-xs rounded-lg border px-2.5 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-50"
                            onClick={() => {
                                if (mode === 'task') {
                                    setSelectedTask(null);
                                    setOpenTaskEditor(true);
                                } else {
                                    setSelectedRisk(null);
                                    setOpenRiskEditor(true);
                                }
                            }}
                        >
                            + New
                        </button>
                    )}
                </div>

                <div className="divide-y rounded border">
                    {list.length === 0 ? (
                        <div className="p-3 text-xs text-gray-500">No linked items.</div>
                    ) : (
                        list.map((x: any) => (
                            <div key={x.id} className="p-3 flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">{x.title}</div>
                                    <div className="text-xs text-gray-600 mt-0.5 truncate">
                                        {mode === 'task'
                                            ? `status: ${x.status ?? '-'} / priority: ${x.priority ?? '-'}`
                                            : `rag: ${x.rag ?? '-'} / score: ${x.score ?? '-'} / status: ${
                                                  x.status ?? '-'
                                              }`}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        className="text-xs rounded px-2 py-1 border hover:bg-gray-50"
                                        onClick={() => {
                                            if (mode === 'task') {
                                                setSelectedTask(x);
                                                setOpenTaskEditor(true);
                                            } else {
                                                setSelectedRisk(x);
                                                setOpenRiskEditor(true);
                                            }
                                        }}
                                    >
                                        Open
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Task Editor (DB) */}
            <TaskEditorModal
                open={openTaskEditor}
                onClose={() => setOpenTaskEditor(false)}
                projectId={projectId}
                task={selectedTask}
                canEdit={canEdit}
                fixedWbsNodeId={item.nodeId}
                onCreate={(input) => apiCreateTask(projectId, input)}
                onUpdate={(taskId, patch) => apiUpdateTask(projectId, taskId, patch)}
                onDelete={(taskId) => apiDeleteTask(projectId, taskId)}
                onSaved={() => {
                    setOpenTaskEditor(false);
                    reload();
                }}
                onDeleted={() => {
                    setOpenTaskEditor(false);
                    reload();
                }}
            />

            {/* Risk Editor (DB) */}
            <RiskEditorModal
                open={openRiskEditor}
                onClose={() => setOpenRiskEditor(false)}
                projectId={projectId}
                risk={selectedRisk}
                canEdit={canEdit}
                fixedWbsNodeId={item.nodeId} // ✅ 핵심: WBS에서 생성/수정시 연결 고정
                onCreate={(input) => apiCreateRisk(projectId, input)}
                onUpdate={(riskId, patch) => apiUpdateRisk(projectId, riskId, patch)}
                onDelete={(riskId) => apiDeleteRisk(projectId, riskId)}
                onSaved={() => {
                    setOpenRiskEditor(false);
                    reload();
                }}
                onDeleted={() => {
                    setOpenRiskEditor(false);
                    reload();
                }}
            />
        </Modal>
    );
}
